/**
 * Price Update Drawer - Detailed view of individual price update
 *
 * Shows:
 * - Price change breakdown
 * - Margin impact calculation
 * - Rule applied
 * - Exception reason (if applicable)
 * - Approve/Reject actions
 */

import React, { useState } from 'react'
import {
  Drawer,
  Space,
  Card,
  Descriptions,
  Typography,
  Tag,
  Button,
  Alert,
  DatePicker,
  Modal,
} from 'antd'
import type { PriceUpdateDrawerProps } from '../types'

const { Text } = Typography

export const PriceUpdateDrawer: React.FC<PriceUpdateDrawerProps> = ({
  open,
  update,
  onClose,
  onApprove,
  onReject,
}) => {
  const [effectiveDateModalOpen, setEffectiveDateModalOpen] = useState(false)
  const [effectiveDate, setEffectiveDate] = useState<string | undefined>()

  if (!update) return null

  const handleApproveClick = () => {
    Modal.confirm({
      title: 'Approve Price Update',
      content: `Approve price change for ${update.productName}?`,
      okText: 'Approve',
      onOk: async () => {
        await onApprove(update.uid)
        onClose()
      },
    })
  }

  const handleApproveWithDate = () => {
    if (!effectiveDate) return

    Modal.confirm({
      title: 'Schedule Price Update',
      content: `Schedule price change for ${update.productName} on ${new Date(effectiveDate).toLocaleDateString()}?`,
      okText: 'Schedule',
      onOk: async () => {
        await onApprove(update.uid, effectiveDate)
        setEffectiveDateModalOpen(false)
        setEffectiveDate(undefined)
        onClose()
      },
    })
  }

  const handleRejectClick = () => {
    Modal.confirm({
      title: 'Reject Price Update',
      content: `Reject price change for ${update.productName}?`,
      okText: 'Reject',
      okType: 'danger',
      onOk: async () => {
        await onReject(update.uid)
        onClose()
      },
    })
  }

  return (
    <>
      <Drawer
        title={`Price Update - ${update.productName}`}
        placement="right"
        width={500}
        open={open}
        onClose={onClose}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Price Change Summary */}
          <Card size="small" title="Price Change">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="SKU">{update.productId}</Descriptions.Item>
              <Descriptions.Item label="Old Price">
                <Text delete type="secondary">
                  ${update.oldPrice.toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="New Price">
                <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                  ${update.newPrice.toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Change">
                <Tag
                  color={update.changePercent > 0 ? 'green' : 'red'}
                  style={{ fontSize: '14px' }}
                >
                  {update.changePercent > 0 ? '+' : ''}
                  {update.changePercent.toFixed(1)}%
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Margin Impact */}
          <Card size="small" title="Margin Impact">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Cost">${update.cost.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Old Margin">
                {update.oldMargin.toFixed(1)}%
              </Descriptions.Item>
              <Descriptions.Item label="New Margin">
                <Text strong style={{ fontSize: '16px' }}>
                  {update.newMargin.toFixed(1)}%
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Margin Change">
                <Tag color={update.newMargin > update.oldMargin ? 'green' : 'red'}>
                  {update.newMargin > update.oldMargin ? '+' : ''}
                  {(update.newMargin - update.oldMargin).toFixed(1)}%
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Rule Applied */}
          <Card size="small" title="Pricing Rule">
            <Text>{update.ruleName}</Text>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Source: {update.source.replace(/_/g, ' ').toUpperCase()}
              </Text>
            </div>
          </Card>

          {/* Exception Reason (if applicable) */}
          {update.status === 'exception' && update.exceptionReason && (
            <Alert
              message="Needs Review"
              description={update.exceptionReason}
              type="warning"
              showIcon
            />
          )}

          {/* Metadata */}
          <Card size="small" title="Details">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Created">
                {new Date(update.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag
                  color={
                    update.status === 'pending'
                      ? 'blue'
                      : update.status === 'exception'
                      ? 'red'
                      : 'green'
                  }
                >
                  {update.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Actions */}
          <Card size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button danger onClick={handleRejectClick}>
                  Reject
                </Button>
                <Space>
                  <Button onClick={() => setEffectiveDateModalOpen(true)}>
                    Schedule...
                  </Button>
                  <Button type="primary" onClick={handleApproveClick}>
                    Approve Now
                  </Button>
                </Space>
              </div>
            </Space>
          </Card>
        </Space>
      </Drawer>

      {/* Effective Date Modal */}
      <Modal
        title="Schedule Price Update"
        open={effectiveDateModalOpen}
        onOk={handleApproveWithDate}
        onCancel={() => {
          setEffectiveDateModalOpen(false)
          setEffectiveDate(undefined)
        }}
        okText="Schedule"
        okButtonProps={{ disabled: !effectiveDate }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Select the date when this price change should take effect:</Text>
          <DatePicker
            style={{ width: '100%' }}
            onChange={(date) => setEffectiveDate(date?.toISOString())}
          />
        </Space>
      </Modal>
    </>
  )
}
